// S-TRUST-SEALS — نمادهای اعتمادِ فوتر (اینماد + نظام صنفی رایانه‌ای).
// فوتر مشترکِ همهٔ صفحاتِ عمومی است (shared.tsx → Footer) پس این دو نماد
// روی هر صفحهٔ عمومی دیده می‌شوند؛ الزامِ اینماد فقط «حداقل صفحهٔ اصلی» است،
// نمایشِ گسترده‌تر مجاز و مطلوب است.
import React from "react";
import { useTranslation } from "react-i18next";

import { BRAND } from "../brand/brand.config";

/**
 * الزام اینماد: عینِ الگویِ صادرشده توسطِ پنلِ اینماد؛ هر تغییری در
 * ویژگی‌ها/ساختار = عدم نمایش نماد. فقط id/Code از `BRAND.trustSeals.enamad`
 * می‌آید (S-TRUST-SEAL-SWAP) — با ریبرندِ دامنه یا صدورِ کدِ تازه فقط آن دو
 * فیلد در brand.config.ts عوض می‌شود، این فایل دست‌نخورده می‌ماند.
 *
 * با dangerouslySetInnerHTML رندر می‌شود نه JSX مستقیم، چون React روی JSX
 * نام‌گذاریِ ویژگی‌ها را camelCase/normalize می‌کند (مثلاً referrerpolicy →
 * referrerPolicy) و در آن صورت کدِ رندرشده دیگر بایت‌به‌بایت همان چیزی نیست
 * که پنلِ اینماد صادر کرده — طبقِ قوانینِ اینماد یعنی نماد باطل/غیرقابل‌نمایش
 * می‌شود. هیچ ویژگی (از‌جمله rel) هرگز نباید به این رشته افزوده شود.
 */
const { id: ENAMAD_ID, code: ENAMAD_CODE } = BRAND.trustSeals.enamad;
export const TRUST_SEAL_ENAMAD =
  `<a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=${ENAMAD_ID}&Code=${ENAMAD_CODE}'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=${ENAMAD_ID}&Code=${ENAMAD_CODE}' alt='' style='cursor:pointer' code='${ENAMAD_CODE}'></a>`;

/**
 * S-ENAMAD-LOCAL — نماد باید «همیشه» دیده شود.
 *
 * سرویسِ `logo.aspx` اینماد برای جفتِ فعلیِ (id,Code) هنوز ۴۰۳ می‌دهد (تشخیصِ
 * S-TRUST-SEAL-SWAP؛ تیکتِ پشتیبانی بازِ مالک) و همین اختلال جای دیگر هم دیده
 * شده. پس تصویر دولایه است: منبعِ اول همان سرویسِ رسمی، و اگر `error` داد
 * **یک‌بار** به کپیِ لوکالِ `/brand/enamad.png` سوییچ می‌شود.
 *
 * چرا imperative و نه onError در JSX: رشتهٔ بالا باید عیناً همان الگویِ
 * صادرشدهٔ پنلِ اینماد بماند (توضیحِ بالا) — تبدیل به JSX باعثِ normalize شدنِ
 * ویژگی‌ها می‌شود (`style='cursor:pointer'` → `style="cursor: pointer"`). پس
 * ساختارِ لینک/ویژگی‌ها دست‌نخورده می‌ماند و فقط `src` (در صورتِ خطا) و `alt`
 * فارسی بعد از mount روی همان گرهٔ DOM ست می‌شوند. هیچ `rel` هرگز.
 */
const ENAMAD_FALLBACK_SRC = "/brand/enamad.png";
// متنِ جایگزینِ تصویر از namespaceِ `shell` می‌آید (فوتر در همهٔ صفحات رندر
// می‌شود، پس nsِ تنبلِ landing اینجا کافی نیست).

function useEnamadImageFallback(altText: string) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  // useLayoutEffect نه useEffect: در همان commit ای که innerHTML ست می‌شود
  // اجرا می‌شود، پس شنونده قبل از هر رویدادِ error شبکه (که همیشه در تسکِ
  // جداگانه‌ای منتشر می‌شود) وصل است — هیچ خطایی از دست نمی‌رود.
  React.useLayoutEffect(() => {
    const img = hostRef.current?.querySelector("img");
    if (!img) return;
    img.alt = altText;
    let swapped = false;
    const swapToLocal = () => {
      // گاردِ حلقهٔ خطا: فقط یک‌بار — اگر لوکال هم خطا داد دیگر تعویض نمی‌شود.
      if (swapped) return;
      swapped = true;
      img.src = ENAMAD_FALLBACK_SRC;
    };
    img.addEventListener("error", swapToLocal);
    return () => img.removeEventListener("error", swapToLocal);
  }, [altText]);
  return hostRef;
}

// نظام صنفی رایانه‌ای استانِ سمنان — پروفایلِ عمومیِ عضویتِ شمارهٔ ۲۳۰۱۰۱۷۷
// (شبکه هوشمند ابتکار ویستا)، یافت‌شده با پیمایشِ فهرستِ عمومیِ اعضا روی
// semnan.irannsr.org (جست‌وجوی توکار سایت پاسخ‌گو نبود). برخلافِ بلوکِ
// اینماد، اینجا محدودیتِ rel وجود ندارد — لینکِ معمولیِ target=_blank است.
const SENF_PROFILE_URL =
  "https://semnan.irannsr.org/fa/trade_unit/2808114/swd_id/107404/اعضای-حقوقی-شبکه-هوشمند-ابتکار-ویستا.html";

export const TrustSeals: React.FC = () => {
  const { t } = useTranslation("shell");
  const enamadRef = useEnamadImageFallback(t("footer.trustSeals.enamadAlt"));
  return (
    <div className="trust-seals">
      <h3 className="trust-seals-title">{t("footer.trustSeals.title")}</h3>
      <div className="trust-seals-row">
        <div className="trust-seal-card">
          {/* eslint-disable-next-line react/jsx-no-target-blank -- الزامِ اینماد: این
              بلوک عیناً رشتهٔ صادرشده توسطِ پنلِ اینماد است و هرگز نباید rel
              (noopener/noreferrer) به آن افزوده شود — افزودنِ rel یعنی خروجیِ
              رندرشده دیگر با کدِ صادرشدهٔ اینماد یکسان نیست و طبقِ قوانینِ اینماد
              نماد باطل می‌شود. این استثنا فقط همین یک بلوک را در بر می‌گیرد. */}
          <div
            ref={enamadRef}
            className="trust-seal-embed trust-seal-embed--enamad"
            dangerouslySetInnerHTML={{ __html: TRUST_SEAL_ENAMAD }}
          />
        </div>
        <div className="trust-seal-card">
          <a
            className="trust-seal-embed trust-seal-embed--senf"
            href={SENF_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/logos/senf-irannsr-140.png"
              alt={t("footer.trustSeals.senfAlt")}
              width={140}
              height={119}
              loading="lazy"
              decoding="async"
            />
          </a>
          <p className="trust-seal-caption">{t("footer.trustSeals.senfCaption")}</p>
        </div>
      </div>
    </div>
  );
};
